// HelpArticlePage — Single help article view
// Layout: hero w/ category, content body, helpful vote, related articles

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface Article {
  category: string;
  title: string;
  readTime: string;
  updated: string;
  views: number;
  helpful: number;
  sections: { heading: string; body: string }[];
}

const ARTICLE: Article = {
  category: 'Getting started',
  title: 'How to activate your first trading challenge',
  readTime: '4 min',
  updated: '2026-07-15',
  views: 8420,
  helpful: 782,
  sections: [
    {
      heading: 'What is a trading challenge?',
      body: 'A trading challenge is a risk-free way to prove your skills. Once you pass, we fund your account with up to $150,000. You keep up to 90% of the profits.',
    },
    {
      heading: '1. Pick your tier',
      body: 'Beginner ($50K account) is the most popular. Mid ($100K) and Professional ($150K) unlock more platforms and features. You can upgrade at any time.',
    },
    {
      heading: '2. Choose your account size',
      body: 'Each tier offers multiple account sizes with different fee structures. Larger accounts have higher success requirements but bigger payouts.',
    },
    {
      heading: '3. Select your platform',
      body: 'We support Deepchart, Rithmic, NinjaTrader, Tradovate, Sierra, Quantower, ATAS, and Bookmap. Pick the one you already use, or activate a new license.',
    },
    {
      heading: '4. Pass the evaluation',
      body: 'Hit the profit target (usually 8-10%) without exceeding the daily loss limit (5%) or max drawdown (10%). Most traders pass in 5-8 funded days.',
    },
    {
      heading: '5. Get funded',
      body: 'Once you pass, you receive a funded account with the same platform. Withdraw profits weekly via ACH, wire, crypto, or PayPal.',
    },
    {
      heading: 'Tips for success',
      body: '• Start small. Most successful traders begin with the $50K account.\n• Trade the same setup daily. Consistency beats complexity.\n• Use our AI Bot Trader with the Breakout strategy — it has a 67% win rate over 412 backtested trades.\n• Stick to your daily loss limit. 95% of blown accounts come from ignoring it.',
    },
  ],
};

export function HelpArticlePage() {
  const nav = useNavigate();
  const { slug } = useParams();
  const [voted, setVoted] = useState<null | 'yes' | 'no'>(null);

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Help</div>
        <button className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>

      {/* Hero */}
      <div className="mx-4 mt-4">
        <div className="rounded-[20px] border border-tf-border bg-white p-5" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #F8F8FA 100%)' }}>
          <div className="text-[11px] uppercase tracking-wider text-tf-text-3">{ARTICLE.category}</div>
          <h1 className="mt-1 text-[24px] font-bold tracking-tight text-tf-text-1 leading-tight">{ARTICLE.title}</h1>
          <div className="mt-3 flex items-center gap-3 text-[11px] text-tf-text-3">
            <span>⏱️ {ARTICLE.readTime} read</span>
            <span>·</span>
            <span>Updated {ARTICLE.updated}</span>
            <span>·</span>
            <span>{ARTICLE.views.toLocaleString()} views</span>
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="mx-4 mt-3 space-y-4">
        {ARTICLE.sections.map((s, i) => (
          <div key={i}>
            <h2 className="text-[18px] font-bold tracking-tight text-tf-text-1">{s.heading}</h2>
            <p className="mt-1.5 text-[15px] leading-relaxed text-tf-text-1 whitespace-pre-line">{s.body}</p>
          </div>
        ))}
      </div>

      {/* Helpful vote */}
      <div className="mx-4 mt-6 rounded-[16px] border border-tf-border bg-white p-4 text-center">
        <div className="text-[14px] font-semibold text-tf-text-1">Was this article helpful?</div>
        <div className="mt-3 flex items-center justify-center gap-3">
          <button
            onClick={() => setVoted('yes')}
            data-testid="helpful-yes"
            className={['flex h-11 items-center gap-2 rounded-full px-5 font-semibold transition',
              voted === 'yes' ? 'bg-tf-profit text-white' : 'bg-tf-surface-1 text-tf-text-1 active:opacity-40'
            ].join(' ')}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9A2 2 0 0 0 19.66 9zM7 22H3a1 1 0 0 1-1-1V11a1 1 0 0 1 1-1h4" strokeLinecap="round" strokeLinejoin="round" /></svg>
            Yes ({ARTICLE.helpful.toLocaleString()})
          </button>
          <button
            onClick={() => setVoted('no')}
            data-testid="helpful-no"
            className={['flex h-11 items-center gap-2 rounded-full px-5 font-semibold transition',
              voted === 'no' ? 'bg-tf-loss text-white' : 'bg-tf-surface-1 text-tf-text-1 active:opacity-40'
            ].join(' ')}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9A2 2 0 0 0 4.34 15zM17 2h4a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1h-4" strokeLinecap="round" strokeLinejoin="round" /></svg>
            No
          </button>
        </div>
        {voted && <p className="mt-2 text-[11px] text-tf-text-3">Thanks for the feedback!</p>}
      </div>

      {/* Contact support */}
      <div className="mx-4 mt-3 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <button onClick={() => alert('Open chat')} className="flex w-full items-center gap-3 px-4 py-3 active:bg-tf-surface-1 transition" data-testid="contact-support">
          <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-blue-tint text-tf-blue text-[18px]">💬</div>
          <div className="flex-1 text-left">
            <div className="text-[14px] font-semibold text-tf-text-1">Still need help?</div>
            <div className="text-[11px] text-tf-text-3">Chat with a real person · ~30 sec</div>
          </div>
          <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>

      {/* Related */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Related articles</h2>
      <div className="mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        {[
          { title: 'What is a funded account?', sub: '3 min · 4.2k views', icon: '💰' },
          { title: 'Choosing the right platform', sub: '5 min · 3.1k views', icon: '🔌' },
          { title: 'How daily drawdown works', sub: '4 min · 2.8k views', icon: '📉' },
        ].map((a, i, arr) => (
          <button key={i} onClick={() => nav('/portal/help-center')} className={['flex w-full items-center gap-3 px-4 py-3 active:bg-tf-surface-1 transition', i < arr.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}>
            <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-blue-tint text-[18px]">{a.icon}</div>
            <div className="flex-1 text-left">
              <div className="text-[14px] font-semibold text-tf-text-1">{a.title}</div>
              <div className="text-[11px] text-tf-text-3">{a.sub}</div>
            </div>
            <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        ))}
      </div>
    </div>
  );
}
